/**
 * Socket.IO Service
 *
 * Manages real-time connection to the server for:
 * - Session tracking
 * - Real-time notifications
 * - Collaborative features
 */

import { io, Socket } from 'socket.io-client';
import env from '../config/env';

// Types for session events
export interface SessionRegisteredEvent {
  socketId: string;
  isDuplicate: boolean;
  existingSessionCount: number;
  timestamp: string;
}

export interface SessionExpiryWarningEvent {
  minutesRemaining: number;
  expiresAt: number;
  message: string;
  canExtend: boolean;
  urgent?: boolean;
}

export interface SessionExpiredEvent {
  message: string;
  timestamp: string;
}

export interface DuplicateLoginEvent {
  newDeviceInfo: {
    userAgent: string;
    ip: string;
    platform?: string;
    browser?: string;
  };
  timestamp: string;
  message: string;
  allowContinue: boolean;
}

export interface ForceLogoutEvent {
  reason: 'new-login' | 'logout-all-devices' | 'terminated-by-user' | 'admin-action' | string;
  message: string;
  newDeviceInfo?: {
    userAgent: string;
    ip: string;
    platform?: string;
    browser?: string;
  };
}

export interface SessionExtendedEvent {
  newExpiry: number;
  message: string;
}

export interface UserSession {
  socketId: string;
  loginTime: number;
  lastActivity: number;
  deviceInfo: {
    userAgent: string;
    ip: string;
    platform?: string;
    browser?: string;
  };
  isCurrent: boolean;
}

// Event callback types
type SessionEventCallback<T> = (data: T) => void;

// Socket instance
let socket: Socket | null = null;
let heartbeatInterval: NodeJS.Timeout | null = null;
let reconnectAttempts = 0;
const MAX_RECONNECT_ATTEMPTS = 10;

// Check if socket should be disabled (for development without server)
const SOCKET_DISABLED = !env.ENABLE_SOCKET;

// Event listeners registry
const eventListeners: Map<string, Set<Function>> = new Map();

/**
 * Get the socket server URL based on environment
 */
function getSocketUrl(): string {
  // Use centralized API base URL from env config
  return env.API_BASE_URL.replace(/\/$/, '');
}

/**
 * Initialize socket connection
 */
export function initializeSocket(): Socket | null {
  // Skip socket initialization if disabled
  if (SOCKET_DISABLED) {
    console.log('[Socket] Disabled in development mode. Set VITE_ENABLE_SOCKET=true to enable.');
    return null;
  }

  if (socket?.connected) {
    return socket;
  }

  const socketUrl = getSocketUrl();
  console.log('[Socket] Connecting to:', socketUrl);

  socket = io(socketUrl, {
    transports: ['websocket', 'polling'],
    // Send the session cookie on the handshake so the server can resolve the
    // stable session id (serverSessionId). That id is how the auth layer binds
    // the login-history row to this socket, which the tab-close / idle-timeout /
    // new-login logout paths need to record a logout reason.
    withCredentials: true,
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: MAX_RECONNECT_ATTEMPTS,
    timeout: 20000,
    autoConnect: true
  });

  // Connection events
  socket.on('connect', () => {
    console.log('[Socket] Connected:', socket?.id);
    reconnectAttempts = 0;
    notifyListeners('connect', { socketId: socket?.id });
  });

  socket.on('disconnect', (reason) => {
    console.log('[Socket] Disconnected:', reason);
    stopHeartbeat();
    notifyListeners('disconnect', { reason });
  });

  socket.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error.message);
    reconnectAttempts++;
    if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.error('[Socket] Max reconnect attempts reached');
      notifyListeners('connection-failed', { error: error.message });
    }
  });

  socket.on('reconnect', (attemptNumber) => {
    console.log('[Socket] Reconnected after', attemptNumber, 'attempts');
    notifyListeners('reconnect', { attempts: attemptNumber });
  });

  // Session events
  socket.on('session:registered', (data: SessionRegisteredEvent) => {
    console.log('[Socket] Session registered:', data);
    startHeartbeat();
    notifyListeners('session:registered', data);
  });

  socket.on('session:expiry-warning', (data: SessionExpiryWarningEvent) => {
    console.log('[Socket] Session expiry warning:', data);
    notifyListeners('session:expiry-warning', data);
  });

  socket.on('session:expired', (data: SessionExpiredEvent) => {
    console.log('[Socket] Session expired:', data);
    stopHeartbeat();
    notifyListeners('session:expired', data);
  });

  socket.on('session:duplicate-login', (data: DuplicateLoginEvent) => {
    console.log('[Socket] Duplicate login detected:', data);
    notifyListeners('session:duplicate-login', data);
  });

  socket.on('session:force-logout', (data: ForceLogoutEvent) => {
    console.log('[Socket] Force logout:', data);
    stopHeartbeat();
    notifyListeners('session:force-logout', data);
  });

  socket.on('session:extended', (data: SessionExtendedEvent) => {
    console.log('[Socket] Session extended:', data);
    notifyListeners('session:extended', data);
  });

  socket.on('session:server-shutdown', (data) => {
    console.log('[Socket] Server shutdown:', data);
    notifyListeners('session:server-shutdown', data);
  });

  return socket;
}

/**
 * Check if socket is disabled
 */
export function isSocketDisabled(): boolean {
  return SOCKET_DISABLED;
}

/**
 * Register user session with the server
 */
export function registerSession(userData: {
  userId: string;
  username: string;
  tokenExpiry: number;
  clientId?: string;
}): void {
  if (SOCKET_DISABLED || !socket?.connected) {
    if (!SOCKET_DISABLED) {
      console.warn('[Socket] Cannot register session: not connected');
    }
    return;
  }
  socket.emit('session:register', userData);
}

/**
 * Extend session with new token expiry
 */
export function extendSession(newTokenExpiry: number): void {
  if (SOCKET_DISABLED || !socket?.connected) {
    if (!SOCKET_DISABLED) {
      console.warn('[Socket] Cannot extend session: not connected');
    }
    return;
  }
  socket.emit('session:extend', { newTokenExpiry });
}

/**
 * Logout current session
 */
export function logoutSession(): void {
  if (socket?.connected) {
    socket.emit('session:logout');
  }
  stopHeartbeat();
}

/**
 * Logout from all devices
 */
export function logoutAllSessions(): void {
  if (socket?.connected) {
    socket.emit('session:logout-all');
  }
  stopHeartbeat();
}

/**
 * Get user's active sessions
 */
export function getActiveSessions(): Promise<{ sessions: UserSession[] }> {
  return new Promise((resolve, reject) => {
    if (SOCKET_DISABLED) {
      resolve({ sessions: [] });
      return;
    }
    if (!socket?.connected) {
      reject(new Error('Socket not connected'));
      return;
    }

    const timeout = setTimeout(() => {
      reject(new Error('Timeout getting sessions'));
    }, 5000);

    socket.emit('session:get-my-sessions', (response: { sessions: UserSession[] }) => {
      clearTimeout(timeout);
      resolve(response);
    });
  });
}

/**
 * Terminate another session
 */
export function terminateSession(targetSocketId: string): void {
  if (SOCKET_DISABLED || !socket?.connected) {
    if (!SOCKET_DISABLED) {
      console.warn('[Socket] Cannot terminate session: not connected');
    }
    return;
  }
  socket.emit('session:terminate-other', targetSocketId);
}

/**
 * Start heartbeat to keep session alive
 */
function startHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
  }

  // Send heartbeat every 2 minutes
  heartbeatInterval = setInterval(() => {
    if (socket?.connected) {
      socket.emit('session:heartbeat');
    }
  }, 2 * 60 * 1000);
}

/**
 * Stop heartbeat
 */
function stopHeartbeat(): void {
  if (heartbeatInterval) {
    clearInterval(heartbeatInterval);
    heartbeatInterval = null;
  }
}

/**
 * Subscribe to socket events
 */
export function subscribeToEvent<T>(event: string, callback: SessionEventCallback<T>): () => void {
  if (!eventListeners.has(event)) {
    eventListeners.set(event, new Set());
  }
  eventListeners.get(event)!.add(callback);

  // Return unsubscribe function
  return () => {
    eventListeners.get(event)?.delete(callback);
  };
}

/**
 * Notify all listeners for an event
 */
function notifyListeners(event: string, data: unknown): void {
  eventListeners.get(event)?.forEach(callback => {
    try {
      callback(data);
    } catch (error) {
      console.error(`[Socket] Error in event listener for ${event}:`, error);
    }
  });
}

/**
 * Join a room
 */
export function joinRoom(roomId: string, userId: string): void {
  if (socket?.connected) {
    socket.emit('join-room', { roomId, userId });
  }
}

/**
 * Leave a room
 */
export function leaveRoom(roomId: string, userId: string): void {
  if (socket?.connected) {
    socket.emit('leave-room', { roomId, userId });
  }
}

/**
 * Send message to a room
 */
export function sendMessage(roomId: string, message: string, userId: string): void {
  if (socket?.connected) {
    socket.emit('message', { roomId, message, userId });
  }
}

/**
 * Check if socket is connected
 */
export function isConnected(): boolean {
  return socket?.connected ?? false;
}

/**
 * Get socket ID
 */
export function getSocketId(): string | undefined {
  return socket?.id;
}

/**
 * Disconnect socket
 */
export function disconnectSocket(): void {
  stopHeartbeat();
  if (socket) {
    socket.disconnect();
    socket = null;
  }
  eventListeners.clear();
}

/**
 * Get socket instance (for advanced usage)
 */
export function getSocket(): Socket | null {
  return socket;
}

/**
 * Subscribe to an ARBITRARY server event, and (optionally) join the rooms it is
 * broadcast to.
 *
 * `subscribeToEvent` above only reaches listeners that this module explicitly
 * re-broadcasts — its `eventListeners` map is fed by the hard-coded `session:*`
 * handlers in `initializeSocket`. A feature event such as `dd:stats-dirty` would
 * therefore be received by the socket and silently dropped. This attaches the
 * handler to the socket itself.
 *
 * Rooms are (re)joined on every connect, not just once: room membership lives on
 * the server side of a single connection, so a reconnect after a dropped network
 * silently leaves the client in no rooms at all — the socket looks healthy while
 * no events ever arrive again.
 */
export function subscribeToRoomEvent<T>(
  event: string,
  rooms: string[],
  callback: (data: T) => void,
): () => void {
  const join = () => {
    if (!socket?.connected) return;
    rooms.forEach((roomId) => socket!.emit('join-room', { roomId, userId: '' }));
  };

  socket?.on(event, callback as (...args: unknown[]) => void);
  socket?.on('connect', join);
  join();

  return () => {
    socket?.off(event, callback as (...args: unknown[]) => void);
    socket?.off('connect', join);
    rooms.forEach((roomId) => {
      if (socket?.connected) socket.emit('leave-room', { roomId, userId: '' });
    });
  };
}
